import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import './index.sass';

import { inDoubleQuotedAttr } from 'xss-filters';
import { useBan } from '../../../context/bannedContent';
import { MESSAGE_CONTENT_TYPE } from '../../../shared/consts';
import { letSendAttachment, normalizeMimeType } from '../../../shared/utility';

import * as actions from '../../../store/actions';
import Button from '../Buttons/Button';
import Text from '../../Atoms/Text';
import ImgSvg from '../../Atoms/ImgSvg';

const messages = defineMessages({
	send: {
		id: 'dragAndDropFileUpload.send',
		defaultMessage: 'Enviar'
	},
	attach: {
		id: 'dragAndDropFileUpload.attach',
		defaultMessage: 'Anexar'
	},
	cancel: {
		id: 'dragAndDropFileUpload.cancel',
		defaultMessage: 'Cancelar'
	},
	attachmentLimitTitle: {
		id: 'dragAndDropFileUpload.attachmentLimitTitle',
		defaultMessage: 'Tamanho de arquivo não suportado'
	},
	attachmentLimitContent: {
		id: 'dragAndDropFileUpload.attachmentLimitContent',
		defaultMessage: 'O arquivo deve ser menor que'
	},
	dragTheFileHere: {
		id: 'dragAndDropFileUpload.dragTheFileHere',
		defaultMessage: 'Arraste o arquivo aqui'
	},
	previewUnavailable: {
		id: 'dragAndDropFileUpload.previewUnavailable',
		defaultMessage: 'Pré-visualização indisponível'
	},
	expandImage: {
		id: 'dragAndDropFileUpload.expandImage',
		defaultMessage: 'Expandir imagem'
	},
	downloadFile: {
		id: 'dragAndDropFileUpload.downloadFile',
		defaultMessage: 'Baixar arquivo'
	},
	bannedImage: {
		id: 'dragAndDropFileUpload.bannedImage',
		defaultMessage: 'Não é possível enviar imagens, para mais informações entre em contato com o administrador da conta.'
	},
	bannedVideo: {
		id: 'dragAndDropFileUpload.bannedVideo',
		defaultMessage: 'Não é possível enviar vídeos, para mais informações entre em contato com o administrador da conta.'
	},
	bannedAudio: {
		id: 'dragAndDropFileUpload.bannedAudio',
		defaultMessage: 'Não é possível enviar áudios, para mais informações entre em contato com o administrador da conta.'
	},
	bannedDocument: {
		id: 'dragAndDropFileUpload.bannedDocument',
		defaultMessage: 'Não é possível enviar documentos, para mais informações entre em contato com o administrador da conta.'
	}
});

const DragAndDropFileUpload = ({
	agent,
	interactionType = '',
	referenceId = '',
	currentInteractionHash,
	addNotification,
	onSendAttachmentMail,
	onSendAttachment,
	setHasFileDragged = () => {},
	newFile,
	open,
	onClose,
	intl
}) => {
	const [files, setFiles] = useState(newFile);
	const [bannedMessage, setBannedMessage] = useState('');
	const { checkIsBanned } = useBan();

	const { getRootProps } = useDropzone({
		maxFiles: 1,
		onDrop: (acceptedFiles) => {
			setFiles(acceptedFiles.map(file => Object.assign(file, {
				preview: URL.createObjectURL(file)
			}))[0]);
		},
		validator: () => {
			setHasFileDragged(true);
		}
	});

	useEffect(() => {
		if (!open) {
			setFiles(null);
		}
	},
	[open]);

	const { formatMessage } = intl;
	const isEmail = ['offcontact', 'email'].includes(interactionType.toLowerCase());

	const getVerifiedContentType = (contentType) => {
		let result = contentType;

		if (result.match(MESSAGE_CONTENT_TYPE.IMAGE)) {
			result = MESSAGE_CONTENT_TYPE.IMAGE;
		} else if (result.match(MESSAGE_CONTENT_TYPE.AUDIO)) {
			result = MESSAGE_CONTENT_TYPE.AUDIO;
		} else if (result.match(MESSAGE_CONTENT_TYPE.VIDEO)) {
			result = MESSAGE_CONTENT_TYPE.VIDEO;
		} else if (result.match(MESSAGE_CONTENT_TYPE.PDF)) {
			result = MESSAGE_CONTENT_TYPE.PDF;
		} else {
			result = MESSAGE_CONTENT_TYPE.DOCUMENT;
		}

		return result;
	};

	useEffect(() => {
		if (!files) return;

		const verifiedContentType = getVerifiedContentType(files.type);

		const messagesMap = {
			image: messages.bannedImage,
			audio: messages.bannedAudio,
			video: messages.bannedVideo,
			document: messages.bannedDocument
		};

		if (verifiedContentType === MESSAGE_CONTENT_TYPE.IMAGE && checkIsBanned('sendImage')) {
			setBannedMessage(messagesMap.image);
		} else if (verifiedContentType === MESSAGE_CONTENT_TYPE.AUDIO && checkIsBanned('sendAudio')) {
			setBannedMessage(messagesMap.audio);
		} else if (verifiedContentType === MESSAGE_CONTENT_TYPE.VIDEO && checkIsBanned('sendVideo')) {
			setBannedMessage(messagesMap.video);
		} else if (
			(verifiedContentType === MESSAGE_CONTENT_TYPE.DOCUMENT || verifiedContentType === MESSAGE_CONTENT_TYPE.PDF)
			&& checkIsBanned('sendDocument')) {
			setBannedMessage(messagesMap.document);
		}
	}, [checkIsBanned, files]);

	const getComponentByType = (messageInfo) => {
		const verifiedContentType = getVerifiedContentType(messageInfo.type);

		switch (verifiedContentType) {
		case MESSAGE_CONTENT_TYPE.IMAGE:
			return (
				<img src={inDoubleQuotedAttr(messageInfo.preview)} alt="media" />
			);
		case MESSAGE_CONTENT_TYPE.AUDIO:
			return (
				<audio src={inDoubleQuotedAttr(messageInfo.preview)} controls><track kind="captions" /></audio>
			);
		case MESSAGE_CONTENT_TYPE.VIDEO:
			return (
				<video src={inDoubleQuotedAttr(messageInfo.preview)} controls><track kind="captions" /></video>
			);
		case MESSAGE_CONTENT_TYPE.PDF:
		case MESSAGE_CONTENT_TYPE.TEXT:
			return (
				<>
					{/* eslint-disable-next-line jsx-a11y/alt-text */}
					<object width="600" height="600" alt="pdf" data={messageInfo.preview} />
				</>
			);
		default:
			return (
				<div className="DragAndDropFileUpload__wrapper__content__file__default">
					<a
						href={inDoubleQuotedAttr(messageInfo.preview)}
						target="_blank"
						download
						rel="noopener noreferrer"
						aria-label={formatMessage(messages.downloadFile)}
					>
						<ImgSvg name="attach-off" />
					</a>
					<Text>{formatMessage(messages.previewUnavailable)}</Text>
				</div>
			);
		}
	};

	const handleClose = () => {
		setFiles(null);
		onClose();
		setHasFileDragged(false);
	};

	const onAttachment = () => {
		const formData = new FormData();

		formData.append('files[]', files);

		if (files) {
			const { name, type, size } = files;
			const { approved, attachmentLimit } = letSendAttachment(size, type, interactionType);

			handleClose();

			if (approved) {
				onSendAttachment({
					formData,
					agent,
					currentInteractionHash,
					fileInfo: {
						name,
						size,
						type: normalizeMimeType(name, type)
					},
					referenceId
				});
			} else {
				addNotification({
					title: formatMessage(messages.attachmentLimitTitle),
					content: `${formatMessage(messages.attachmentLimitContent)} ${attachmentLimit.size}MB`,
					type: 'warning'
				});
			}
		}
	};

	const onAttachmentEmail = () => {
		const { type, size } = files;
		const { approved, attachmentLimit } = letSendAttachment(size, type, 'EMAIL');

		handleClose();

		if (approved) {
			const formData = new FormData();

			formData.append('files[]', files);

			onSendAttachmentMail({
				accountId: agent.info.account.id,
				interactionHash: currentInteractionHash,
				formData
			});
		} else {
			addNotification({
				title: formatMessage(messages.attachmentLimitTitle),
				content: `${formatMessage(messages.attachmentLimitContent)} ${attachmentLimit.size}MB`,
				type: 'warning'
			});
		}
	};

	return (
		<div style={{ height: open ? 'calc(100vh - 65px)' : 0, bottom: newFile ? -10 : 0 }} className="DragAndDropFileUpload">
			{
				!files ? (
					<div {...getRootProps({ className: 'dropzone' })}>
						<span>{formatMessage(messages.dragTheFileHere)}</span>
					</div>
				) : (
					<div style={{ height: 'calc(100vh - 165px)' }}>
						<div className="DragAndDropFileUpload__header">
							<Text>{files.name}</Text>
							<button
								onClick={handleClose}
								type="button"
							>
								<ImgSvg name="close" />
							</button>
						</div>
						{bannedMessage && (
							<div className="DragAndDropFileUpload__alert alert is-danger">
								<div className="alert-content">
									<span className="icon is-medium">
										<i className="fa-regular fa-circle-minus" />
									</span>
									<p>{formatMessage(bannedMessage)}</p>
								</div>
							</div>
						)}
						<div className="DragAndDropFileUpload__wrapper">
							<div className="DragAndDropFileUpload__wrapper__content">
								<div className="DragAndDropFileUpload__wrapper__content__file">
									{getComponentByType(files)}
								</div>
							</div>
						</div>
						<div className="DragAndDropFileUpload__actions">
							<Button secondary click={handleClose}>{formatMessage(messages.cancel)}</Button>
							{isEmail
								? <Button click={onAttachmentEmail}>{formatMessage(messages.attach)}</Button>
								: <Button active={!bannedMessage} click={onAttachment}>{formatMessage(messages.send)}</Button>
							}
						</div>
					</div>
				)
			}
		</div>
	);
};

DragAndDropFileUpload.propTypes = {
	agent: PropTypes.shape({
		info: PropTypes.shape({
			account: PropTypes.shape({
				id: PropTypes.number
			})
		})
	}).isRequired,
	currentInteractionHash: PropTypes.string.isRequired,
	open: PropTypes.bool.isRequired,
	interactionType: PropTypes.string,
	onClose: PropTypes.func.isRequired,
	setHasFileDragged: PropTypes.func,
	onSendAttachment: PropTypes.func.isRequired,
	onSendAttachmentMail: PropTypes.func.isRequired,
	addNotification: PropTypes.func.isRequired,
	newFile: PropTypes.shape({}),
	referenceId: PropTypes.string,
	intl: PropTypes.shape({
		formatMessage: PropTypes.func.isRequired
	}).isRequired
};

const mapStateToProps = state => ({
	agent: state.agent,
	currentInteractionHash: state.interaction.currentInteractionHash
});

const mapActionsToProps = dispatch => ({
	onSendAttachmentMail: info => dispatch(actions.sendEmailAttachment(info)),
	onSendAttachment: info => dispatch(actions.sendAttachment(info)),
	addNotification: info => dispatch(actions.addNotification(info))
});

export default connect(mapStateToProps, mapActionsToProps)(injectIntl(DragAndDropFileUpload));
